import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Chip,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Home, CheckCircle } from "@mui/icons-material";
import { useCreateOrUpdatePatient } from "../../../stores/Patients/patient.mutations";
import { PatientAddressMatch } from "./useExtractionMatching";
import { AddressDto, AddressDtoTypeEnum } from "../../../api-client";

// ============================================================================
// Types
// ============================================================================

interface PatientAddressMatchDialogProps {
  open: boolean;
  onClose: () => void;
  match: PatientAddressMatch;
  patientId: number;
}

// ============================================================================
// Helpers
// ============================================================================

/** Best-effort parse of a free-text address into structured fields */
function parseExtractedAddress(address: string): {
  addressLine1: string;
  zip: string;
  city: string;
} {
  // Try pattern: "Street 123, 12345 City" or "Street 123\n12345 City"
  // Also handles country prefix like "D 38118 Braunschweig" or "D-38118 Braunschweig"
  const lines = address.split(/[,\n]/).map((l) => l.trim()).filter(Boolean);

  if (lines.length >= 2) {
    const street = lines[0];
    // Strip optional country prefix (D, DE, A, AT, CH etc.) before zip
    const zipLine = lines[lines.length - 1].replace(/^[A-Z]{1,3}[-\s]+/, "");
    const zipCityMatch = zipLine.match(/^(\d{4,5})\s+(.+)/);
    if (zipCityMatch) {
      return { addressLine1: street, zip: zipCityMatch[1], city: zipCityMatch[2] };
    }
    return { addressLine1: street, zip: "", city: lines[lines.length - 1] };
  }

  // Single line: try to extract zip+city from end (with optional country prefix)
  const singleLineMatch = address.match(/^(.+?)\s+(?:[A-Z]{1,3}[-\s]+)?(\d{4,5})\s+(.+)$/);
  if (singleLineMatch) {
    return { addressLine1: singleLineMatch[1], zip: singleLineMatch[2], city: singleLineMatch[3] };
  }

  return { addressLine1: address, zip: "", city: "" };
}

// ============================================================================
// Component
// ============================================================================

const PatientAddressMatchDialog: React.FC<PatientAddressMatchDialogProps> = ({
  open,
  onClose,
  match,
  patientId,
}) => {
  const updatePatient = useCreateOrUpdatePatient();

  const parsed = useMemo(
    () => (match.extractedAddress ? parseExtractedAddress(match.extractedAddress) : null),
    [match.extractedAddress]
  );

  const [addressFields, setAddressFields] = useState({
    addressLine1: parsed?.addressLine1 || "",
    zip: parsed?.zip || "",
    city: parsed?.city || "",
  });

  // Re-sync when dialog opens with new data
  React.useEffect(() => {
    if (open && parsed) {
      setAddressFields({
        addressLine1: parsed.addressLine1,
        zip: parsed.zip,
        city: parsed.city,
      });
    }
  }, [open, parsed]);

  const handleSave = () => {
    if (!match.patient) return;

    const newAddress: AddressDto = {
      contactInfo: {
        addressLine1: addressFields.addressLine1,
        city: addressFields.city,
        zip: addressFields.zip,
      },
      type: AddressDtoTypeEnum.Haus,
      isPrimary: false,
      isVisit: false,
      isAlternativeBilling: false,
    };

    const updatedPatient = {
      ...match.patient,
      addresses: [...(match.patient.addresses || []), newAddress],
    };

    updatePatient.mutate(updatedPatient as any, {
      onSuccess: () => onClose(),
    });
  };

  const saving = updatePatient.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Patientenadresse</DialogTitle>

      <DialogContent dividers>
        {/* Show extracted address */}
        <Alert severity="info" icon={<Home />} sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={500}>
            Extrahierte Adresse:
          </Typography>
          <Typography variant="body2">{match.extractedAddress}</Typography>
        </Alert>

        {/* Existing addresses comparison */}
        {match.patient?.addresses && match.patient.addresses.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Vorhandene Adressen:
            </Typography>
            {match.patient.addresses.map((addr, i) => {
              const ci = addr.contactInfo;
              const addrText = [ci?.addressLine1, ci?.zip, ci?.city]
                .filter(Boolean)
                .join(", ");
              const isMatch = match.matchedAddress?.id === addr.id;
              return (
                <Box
                  key={addr.id || i}
                  sx={{
                    p: 1,
                    mb: 0.5,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: isMatch ? "success.main" : "divider",
                    bgcolor: isMatch ? "success.50" : undefined,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {isMatch && <CheckCircle color="success" fontSize="small" />}
                  <Box>
                    <Typography variant="body2">{addrText || "—"}</Typography>
                    <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
                      {addr.isPrimary && <Chip label="Meldeadresse" size="small" variant="outlined" />}
                      {addr.isVisit && <Chip label="Anfahrt" size="small" variant="outlined" />}
                      {addr.type && <Chip label={addr.type} size="small" variant="outlined" />}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Editable fields for new address */}
        {match.status !== "matched" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography variant="subtitle2">Neue Adresse anlegen:</Typography>
            <TextField
              size="small"
              label="Straße & Hausnr."
              value={addressFields.addressLine1}
              onChange={(e) => setAddressFields((p) => ({ ...p, addressLine1: e.target.value }))}
              fullWidth
            />
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                size="small"
                label="PLZ"
                value={addressFields.zip}
                onChange={(e) => setAddressFields((p) => ({ ...p, zip: e.target.value }))}
                sx={{ width: 120 }}
              />
              <TextField
                size="small"
                label="Ort"
                value={addressFields.city}
                onChange={(e) => setAddressFields((p) => ({ ...p, city: e.target.value }))}
                fullWidth
              />
            </Box>
          </Box>
        )}

        {match.status === "matched" && (
          <Alert severity="success" sx={{ mt: 1 }}>
            Die extrahierte Adresse stimmt mit einer vorhandenen Adresse überein.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {match.status === "matched" ? "Schließen" : "Abbrechen"}
        </Button>
        {match.status !== "matched" && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !addressFields.addressLine1}
            startIcon={saving ? <CircularProgress size={16} /> : undefined}
          >
            {saving ? "Wird gespeichert…" : "Adresse speichern"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PatientAddressMatchDialog;
